import { useNavigate } from 'react-router-dom';
import classNames from 'classnames/bind';
import styles from './Buttons.module.css';
import { nanoid } from '@reduxjs/toolkit';

import { useAppDispatch, useAppSelector } from '../../../hooks/useRedux';
import { useSlice } from '../../../hooks/useSlice';
import { usePage } from '../../../hooks/usePage';
import useModal from '../../../hooks/useModal';

import { selectMenu } from '../../../features/infoSlice';
import { libHistoryChanged } from '../../../features/librarySlice';
import { shareHistoryChanged } from '../../../features/shareSlice';

import { libApiSlice } from '../../../services/library';
import { shareApiSlice } from '../../../services/share';
import { useAddSharedLibraryMutation } from '../../../services/search';
import { useRestoreMutation } from '../../../services/trash';
import { useCountHitsMutation } from '../../../services/common';

import Button from '../../Button';

import {
  EBOOK_OPEN,
  EBOOK_EDIT,
  DETAILS,
  DOWNLOAD,
  RESTORE,
  OPEN,
  SHARED_LIBRARY,
  REGISTERED_SHARED_LIBRARY,
  LIBRARY,
  DRIVE,
  REGISTRATION,
  EBOOK_SHARE,
} from '../../../constants';
import { Options } from '../../../types/library';

import { openSelpubCloud } from '../../../utils/selpub';
import { download, isMobile, openNewWindow } from '../../../utils/util';

import toast from 'react-hot-toast';

interface Buttons {
  id?: number;
  entity?: any;
  item?: any;
  isShared?: boolean;
}

export default function Buttons({ id = 0, entity, item, isShared }: Buttons) {
  const dispatch = useAppDispatch();
  const cx = classNames.bind(styles);
  const { detailsToggled, itemSelected, selectOptions, selectSelectedId } =
    useSlice();
  const { toggleModal } = useModal();
  const menu = useAppSelector(selectMenu);
  const selectedId = useAppSelector(selectSelectedId);
  const { layoutType, isDetailsOpened, sortType, sortOrder }: Options =
    useAppSelector(selectOptions);
  const { initPageList, pageChanged } = usePage();
  const navigate = useNavigate();

  const isMyLibrary = menu === 'library';
  const isMyDrive = menu === 'mydrive';
  const isSearch = menu === 'search';

  const [restore] = useRestoreMutation();
  const [getHistory] = isMyLibrary
    ? libApiSlice.useLazyGetLibHistoryQuery()
    : shareApiSlice.useLazyGetSharedHistoryQuery();
  const [addSharedLibrary] = useAddSharedLibraryMutation();
  const [countHits] = useCountHitsMutation();

  const selectItem = () => {
    dispatch(itemSelected({ ids: [id], item: { ...entity } }));
  };

  const isFolder = item?.ext === 'folder';
  const firstBtnName = isMyDrive
    ? isFolder || item?.ext === 'web'
      ? OPEN
      : DOWNLOAD
    : menu === 'trash'
      ? RESTORE
      : EBOOK_OPEN;
  const secondBtnName = isSearch ? REGISTRATION : EBOOK_EDIT;
  const hasSecondBtn = isMyLibrary || (isSearch && !isShared);

  const initPage = () => {
    dispatch(pageChanged(0));
    dispatch(
      initPageList({
        page: 0,
        sortBy: sortType,
        sortDir: sortOrder,
        id: nanoid(),
      })
    );
  };

  const restoreTrash = async () => {
    const tags: { [key: string]: 'Library' | 'Share' | 'Drive' } = {
      [LIBRARY]: 'Library',
      [SHARED_LIBRARY]: 'Share',
      [DRIVE]: 'Drive',
    };
    const tag = tags[entity.resource.location];
    try {
      await restore({ seq: id, tag }).unwrap();
      initPage();
    } catch (error) {
      console.log('Failed to restore');
    }
  };

  const clickFirstBtn = async (e: React.MouseEvent) => {
    e.stopPropagation();

    const openEbook = async () => {
      await countHits(id).unwrap();
      openSelpubCloud('viewer', id);

      if (isMyLibrary || menu === 'share') {
        setTimeout(async () => {
          try {
            await getHistory(id)
              .unwrap()
              .then((res: any) => {
                const historyList = res.data;
                isMyLibrary
                  ? dispatch(libHistoryChanged(historyList))
                  : dispatch(shareHistoryChanged(historyList));

                initPage();
              });
          } catch (error) {
            console.log('Fail to get history', error);
          }
        }, 3000);
      }
    };

    switch (firstBtnName) {
      case OPEN:
        if (isMyDrive) {
          if (isFolder) {
            navigate(`folders/${item.folderId}`, {
              state: { folderId: item.folderId, folderName: item.orgFileName },
            });
          } else {
            const url = entity.resource.url;
            openNewWindow(url);
          }
        } else {
          openEbook();
        }
        break;
      case DOWNLOAD:
        download(id);
        break;
      case RESTORE:
        restoreTrash();
        return;
      default:
        openEbook();
        break;
    }
    selectItem();
  };

  const clickSecondBtn = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!isMobile) {
      isSearch ? registerSharedLib() : openSelpubCloud('creator', id);
      selectItem();
    }
  };

  const registerSharedLib = async () => {
    try {
      await addSharedLibrary(id).unwrap();
      toast(REGISTERED_SHARED_LIBRARY);
      initPage();
    } catch (error) {
      console.log('Failed to addSharedLibrary : ', error);
    }
  };

  const openDetails = (e: React.MouseEvent) => {
    e.stopPropagation();

    const shouldClose = selectedId === id && isDetailsOpened;
    selectItem();
    dispatch(detailsToggled(!shouldClose));
  };

  const openShareModal = (e: React.MouseEvent) => {
    e.stopPropagation();

    selectItem();
    toggleModal({
      name: `projectShare`,
      id: 'projectShare',
      type: 'alert',
    });
  };

  return (
    <div className={cx('view_area', layoutType, { no_list: !item })}>
      <Button
        name={firstBtnName}
        classnames={[
          'b_t',
          'ebook_open',
          ...[layoutType === 'list' && entity.isExp && 'is--disabled'],
        ]}
        handleClick={clickFirstBtn}
      />
      {hasSecondBtn && (
        <Button
          name={secondBtnName}
          classnames={['b_t', 'dull', 'ebook_edit']}
          handleClick={clickSecondBtn}
        ></Button>
      )}
      {isMyLibrary && layoutType === 'grid' && (
        <Button
          name={EBOOK_SHARE}
          classnames={['b_t', 'dull', 'ebook_edit']}
          handleClick={openShareModal}
        ></Button>
      )}
      {layoutType === 'list' && item && (
        <Button
          name={DETAILS}
          classnames={['b_t', 'dull', 'ebook_details']}
          handleClick={openDetails}
        />
      )}
    </div>
  );
}
